import * as React from "react";

import { Currency } from "@krea/shared/types/common";

import { cn } from "../../utils";
import { InputGroup, InputGroupInput } from "./input-group";
import { useNumericInput } from "./use-numeric-input";

const CURRENCY_LOCALE: Record<Currency, string> = {
  [Currency.SEK]: "sv",
  [Currency.EUR]: "fi",
};

const CURRENCY_LABEL: Record<Currency, string> = {
  [Currency.SEK]: "kr",
  [Currency.EUR]: "€",
};

export type PercentOrMoneyMode = "percent" | "amount";

const MODES: PercentOrMoneyMode[] = ["percent", "amount"];

interface IPercentOrMoneyInputProps
  extends Omit<
    React.ComponentPropsWithoutRef<"input">,
    "onChange" | "value" | "onBlur"
  > {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  mode: PercentOrMoneyMode;
  onModeChange: (mode: PercentOrMoneyMode) => void;
  currency: Currency;
}

interface IHighlighterStyle {
  height: number;
  width: number;
  transform: string;
}

const PercentOrMoneyInput = ({
  value,
  onChange,
  onBlur,
  onFocus,
  mode,
  onModeChange,
  currency,
  disabled,
  ref,
  ...props
}: IPercentOrMoneyInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [currency],
  );

  const moneyFormat = React.useCallback(
    (val: number) => currencyFormatter.format(val),
    [currencyFormatter],
  );

  const { displayValue, handleFocus, handleChange, handleBlur } =
    useNumericInput({
      value,
      onChange,
      onBlur,
      onFocus,
      type: mode === "percent" ? "float" : "integer",
      format: mode === "amount" ? moneyFormat : undefined,
    });

  const currencyLabel = CURRENCY_LABEL[currency];
  const activeIndex = mode === "percent" ? 0 : 1;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLLabelElement | null)[]>([]);

  const [highlighterStyle, setHighlighterStyle] =
    React.useState<IHighlighterStyle>({
      height: 0,
      width: 0,
      transform: "translate(0, 0)",
    });

  const updateHighlighter = React.useCallback(() => {
    const selected = optionRefs.current[activeIndex];
    const container = containerRef.current;
    if (!selected || !container) return;

    const containerRect = container.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);

    const paddingLeft = Number.parseFloat(containerStyle.paddingLeft);
    const paddingTop = Number.parseFloat(containerStyle.paddingTop);
    const borderLeft = Number.parseFloat(containerStyle.borderLeftWidth);
    const borderTop = Number.parseFloat(containerStyle.borderTopWidth);

    const translateX =
      selectedRect.left - containerRect.left - paddingLeft - borderLeft;
    const translateY =
      selectedRect.top - containerRect.top - paddingTop - borderTop;

    setHighlighterStyle({
      height: selectedRect.height,
      width: selectedRect.width,
      transform: `translate(${translateX}px, ${translateY}px)`,
    });
  }, [activeIndex]);

  React.useLayoutEffect(() => {
    updateHighlighter();
  }, [updateHighlighter]);

  React.useEffect(() => {
    const observer = new ResizeObserver(updateHighlighter);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [updateHighlighter]);

  const cycleMode = React.useCallback(() => {
    if (disabled) return;
    onModeChange(MODES[(activeIndex + 1) % MODES.length] as PercentOrMoneyMode);
  }, [disabled, activeIndex, onModeChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      onModeChange(
        MODES[(activeIndex + 1) % MODES.length] as PercentOrMoneyMode,
      );
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      onModeChange(
        MODES[
          (activeIndex - 1 + MODES.length) % MODES.length
        ] as PercentOrMoneyMode,
      );
    }
  };

  const groupName = React.useId();

  return (
    <InputGroup className="overflow-hidden">
      <InputGroupInput
        ref={ref}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode={mode === "percent" ? "decimal" : "numeric"}
        disabled={disabled}
        {...props}
      />
      <div className="flex items-center border-l border-gray-300 px-1.5">
        <div
          ref={containerRef}
          role="radiogroup"
          aria-label="Value mode"
          onKeyDown={handleKeyDown}
          onClick={cycleMode}
          // Same segmented-control treatment as Tabs (gray-100 track, raised
          // white pill with a primary-200 ring, primary label for the active
          // option), at the compact size this needs to sit inside an input.
          className={cn(
            "relative flex items-center rounded-lg bg-gray-100 p-1",
            !disabled && "cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <div
            aria-hidden="true"
            // The ring is what makes the active option legible against the
            // track — TabsList carries the same one (KREA-4503 found a plain
            // white pill too subtle to read at a glance).
            className="absolute rounded-md bg-white shadow-md ring-1 ring-primary-200 transition-[transform,width,height] duration-200 ease-in-out"
            style={highlighterStyle}
          />
          {MODES.map((opt) => {
            const label = opt === "percent" ? "%" : currencyLabel;
            const isSelected = mode === opt;
            const optIndex = MODES.indexOf(opt);
            return (
              <label
                key={opt}
                ref={(el) => {
                  optionRefs.current[optIndex] = el;
                }}
                // cursor-pointer belongs HERE, not only on the track: the
                // labels cover nearly all of it, and a <label> does not
                // inherit the track's cursor — measured `default` on hover,
                // which is the whole reason the control read as non-clickable.
                className={cn(
                  "relative z-10 flex items-center rounded-md px-3 py-1 text-sm font-semibold select-none transition-colors duration-200",
                  isSelected ? "text-primary" : "text-gray-600",
                  !disabled && "cursor-pointer",
                  !disabled && !isSelected && "hover:text-primary",
                  disabled && "cursor-not-allowed",
                )}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={opt}
                  checked={isSelected}
                  disabled={disabled}
                  tabIndex={isSelected && !disabled ? 0 : -1}
                  aria-label={`${label} mode`}
                  onChange={() => {}}
                  onClick={(e) => e.stopPropagation()}
                  className="sr-only"
                />
                {label}
              </label>
            );
          })}
          <div aria-live="polite" className="sr-only">
            {mode === "percent" ? "%" : currencyLabel} selected
          </div>
        </div>
      </div>
    </InputGroup>
  );
};

PercentOrMoneyInput.displayName = "PercentOrMoneyInput";

export type { IPercentOrMoneyInputProps };
export { PercentOrMoneyInput };
